/**
 * "Who's here" gadget (Team): the other people in the project now and what they are on, the AI agents here, and how many
 * messages for you are unread (the project's notes, Messages' own rule: someone else's, in your conversations, newer
 * than when that conversation was last on your screen in this browser). Message… and Messages for everyone (a viewer
 * chats too).
 */
import React from 'react';
import PropTypes from 'prop-types';
import { membersOf, readMarks } from '../../../utils/messagesModel';
import { hereNow, unreadCount } from '../../../utils/homeGadgets/team';
import { ParticipantMark } from '../../presence/Presence';
import GadgetCard from './GadgetCard';
import { useAccess, useNotes } from './sources';

const SHOWN = 5;

export default function PeopleGadget({ projectId, presence = null, onOpenMessages }) {
  const { people, agents } = hereNow(presence);
  const notes = useNotes(projectId);
  const access = useAccess(projectId);
  const me = access.data?.you || (presence?.you ? { sub: presence.you.sub || null, email: null } : null);
  const unread = notes.data ? unreadCount(notes.data.notes || [], me, readMarks(projectId), membersOf(access.data, me)) : null;
  let messagesLine;
  if (unread !== null) { messagesLine = unread > 0 ? <strong className="gd-num" data-testid="people-unread">{unread} unread {unread === 1 ? 'message' : 'messages'} for you</strong> : 'No unread message for you.'; }
  else if (notes.error) { messagesLine = 'Your messages could not be read just now.'; }
  else { messagesLine = 'Reading your messages…'; }
  const connecting = !presence || presence.status === 'connecting';
  return (
    <GadgetCard meta={people.length + agents.length > 0 ? `${people.length + agents.length} here` : null} testId="people-gadget" title="Who's here">
      {people.length === 0 && agents.length === 0 ? (
        <p className="ph-quiet" data-testid="gadget-empty">{connecting ? 'Looking for who is here…' : 'Nobody else is here now.'}</p>
      ) : (
        <ul className="gd-rows">
          {people.slice(0, SHOWN).map((p) => (
            <li className="gd-row" data-testid={`people-${p.id}`} key={p.id}>
              <ParticipantMark idle={p.idle} initials={p.initials} label={p.name} size={20} slot={p.slot} />
              <strong>{p.name}{p.viewer ? ' (viewer)' : ''}</strong>
              <span className="gd-grow ph-muted">{p.activity}{p.idle ? ' · idle' : ''}</span>
            </li>
          ))}
          {people.length > SHOWN && <li className="gd-row ph-muted">and {people.length - SHOWN} more</li>}
          {agents.length > 0 && (
            <li className="gd-row" data-testid="people-agents">
              <ParticipantMark agent initials="AI" label="AI agents" size={20} slot={0} />
              <span className="gd-grow">{agents.length === 1 ? agents[0].name : `${agents.length} AI agents`}</span>
            </li>
          )}
        </ul>
      )}
      <p className="gd-note" data-testid="people-messages">{messagesLine}</p>
      <div className="gd-actions">
        <button className="ph-btn ph-btn-small" data-testid="people-message" onClick={() => onOpenMessages({ projectId, view: 'compose' })} type="button">Message…</button>
        <button className="ph-link" data-testid="people-messages-open" onClick={() => onOpenMessages({ projectId, view: 'list' })} type="button">Messages</button>
      </div>
    </GadgetCard>
  );
}

PeopleGadget.propTypes = {
  // ({ projectId, view }) => void: Messages (collab/Messages openMessages).
  onOpenMessages: PropTypes.func.isRequired,
  presence: PropTypes.object,
  projectId: PropTypes.string.isRequired,
};
